/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert } from "@mui/material";
import { z } from "zod";
import { Node } from "@xyflow/react";

import {
	ScenarioGraph,
	ScenarioGraphData,
} from "@/pages/Private/pages/Courses/components/Editor/ScenarioGraph";

import { PhaseData } from "@/pages/Private/pages/Courses/components/Editor/NodeDataTyps";

interface IJsonEditorProps {
	value: any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onChange: (value: any) => void;
	courseId?: number;
	containerClassName?: string;
	editorsClassName?: string;
	externalErrorMessage?: string;
	onNodeClick?: (event: React.MouseEvent, node: Node) => void;
	onPaneClick?: (event: React.MouseEvent) => void;
	onConnectionNodeSelect?: (node: Node<PhaseData>, updatedData: ScenarioGraphData) => void;
}

export function ensureValidPositions(data: any): any {
	if (!data?.Content || !Array.isArray(data.Content)) {
		return data;
	}

	return {
		...data,
		Content: data.Content.map((node: any, index: number) => {
			const hasValidPosition =
				node.position && typeof node.position.x === "number" && typeof node.position.y === "number";

			return {
				...node,
				position: hasValidPosition ? node.position : { x: 100 + index * 50, y: 100 + index * 50 },
			};
		}),
	};
}

export default function CustomJsonEditor({
	value,
	onChange,
	courseId,
	containerClassName = "",
	externalErrorMessage = "",
	onNodeClick,
	onPaneClick,
	onConnectionNodeSelect,
}: IJsonEditorProps) {
	const { t } = useTranslation();
	const ts = (key: string) => t(`courses.${key}`);
	const [isShowActionButton, setIsShowActionButton] = useState(false);

	const [unsavedJSONChanges, setUnsavedJSONChanges] = useState<ScenarioGraphData | null>(null);

	const [loading, setLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState(externalErrorMessage);

	const isValidPositioned = useMemo(() => {
		if (!value?.Content || !Array.isArray(value.Content)) {
			return value;
		}

		const valid = value.Content.map((node: any) => {
			const hasValidPosition =
				node.position && typeof node.position.x === "number" && typeof node.position.y === "number";

			return hasValidPosition;
		});

		return valid.every((v: boolean) => v);
	}, [value]);

	useEffect(() => {
		if (value) {
			const parsedValue = typeof value === "string" ? JSON.parse(value) : value;
			const validated = ensureValidPositions(parsedValue);

			setIsShowActionButton(false);

			setUnsavedJSONChanges(validated);
			setLoading(false);
		}
	}, [value]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const onSaveChanges = (val: any) => {
		const parsedValue = val;

		// Pflichtfelder ergänzen
		if (!parsedValue.scenarioName) {
			parsedValue.scenarioName = ts("UntitledScenario");
		}

		try {
			// Positionen ergänzen, falls ungültig
			const corrected = ensureValidPositions(parsedValue);

			setErrorMessage("");
			setIsShowActionButton(false);

			onChange(corrected);
		} catch (e) {
			if (e instanceof z.ZodError) {
				setErrorMessage(
					"Invalid course scenario structure: " +
						e.issues
							.map(
								(err: { path: PropertyKey[]; message: string }) =>
									err.path.join("->") + " " + err.message
							)
							.join(", ")
				);
			} else {
				setErrorMessage("Invalid JSON format or structure");
			}
		}
	};

	// useEffect für asynchrones Aufrufen von onSaveChanges, um setState im Render zu vermeiden
	useEffect(() => {
		if (isShowActionButton && unsavedJSONChanges) {
			onSaveChanges(unsavedJSONChanges);
		}
		// eslint-disable-next-line
	}, [isShowActionButton, unsavedJSONChanges]);

	const graphView = (
		<div className={`space-y-1 flex flex-col h-[100%]`}>
			<ScenarioGraph
				courseId={courseId}
				data={unsavedJSONChanges as ScenarioGraphData}
				isValidPositioned={isValidPositioned}
				onChange={(updatedData) => {
					const filtered = ensureValidPositions({
						...value,
						...updatedData,
						Content: updatedData.Content.filter((node: any) => node?.id !== null),
					});

					setUnsavedJSONChanges(filtered);
					setIsShowActionButton(true);
				}}
				onConnectionNodeSelect={onConnectionNodeSelect}
				onNodeClick={onNodeClick}
				onPaneClick={onPaneClick}
			/>
		</div>
	);

	const component = (
		<>
			{!loading && unsavedJSONChanges ? graphView : <p>Loading...</p>}

			{errorMessage && (
				<div className="absolute bottom-0 w-full max-h-[100px] overflow-auto rounded-lg">
					<Alert
						severity="error"
						sx={{
							alignItems: "center",
							bgcolor: "error.light",
							fontSize: 14,
						}}
					>
						{errorMessage}
					</Alert>
				</div>
			)}
		</>
	);

	return <div className={`w-full relative ${containerClassName}`}>{component}</div>;
}
